import './WelcomeMessage.sass';

import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BigNumber from 'bignumber.js';

import {
  BottomBar,
  Button,
  Form,
  FormItem,
  Header,
  Input,
  List,
  ListItem,
  Placeholder,
  ScrollView,
  Switch,
} from '$uikit';

import { Loader } from '$shared/components';
import { useTranslator } from '$hooks';
import { AttachPickerManagerRef } from '$shared/components/AttachPickerManager/AttachPickerManager.interface';
import { imActions, imSelector } from '$store/im';
import AttachPickerManager from '$shared/components/AttachPickerManager/AttachPickerManager';
import { IcGallery28, IcVideo28 } from '$assets';
import { formatInputAmount } from '$utils/number';
import { isBackNavigation } from '$navigation/router';
import { mainSelector } from '$store/main';
import { goTo } from '$navigation/helper';
import { SETTINGS, WELCOME_MESSAGE } from '$shared/constants/pages';
import { formatCurrency } from '$utils';
import { CurrencyEnum } from '$store/models';
import { useRoute, useRouter } from 'react-router5';
import { WelcomeMessageType } from '$store/im/interface';
import { attachFullItemsSelector, attachItemsSelector } from '$store/attach';
import { RootState } from '$store/rootReducer';
import classNames from 'classnames';

const WelcomeMessageInner: FC<{ type: WelcomeMessageType }> = ({ type }) => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const isBack = isBackNavigation();
  const [photoInputKey, setPhotoInputKey] = useState(0);
  const [videoInputKey, setVideoInputKey] = useState(0);
  const { mVerified, feePercent } = useSelector(mainSelector);
  const attachManager = useRef<AttachPickerManagerRef | null>(null);
  const { tonRate, welcomeMessage: welcomeMessageForms } = useSelector(imSelector);

  const welcomeMessage = welcomeMessageForms[type];
  const { price, loadingStatus } = welcomeMessage;
  const attachments = useSelector((state: RootState) =>
    attachFullItemsSelector(state, 'welcome_message_' + type),
  );

  const loadData = useCallback(() => {
    if (welcomeMessage.loadingStatus !== 'loaded') {
      dispatch(imActions.loadWelcomeMessage(type));
    }
  }, [type]);

  useEffect(() => {
    dispatch(imActions.loadTonRate());
    if (!isBack) {
      loadData();
    }
    return () => {
      dispatch(
        imActions.setWelcomeMessageLoading({
          type,
          loading: 'loading',
        }),
      );
    };
  }, [type]);

  useEffect(() => {
    if (!mVerified) {
      goTo(SETTINGS);
    }
  }, [mVerified]);

  const handlePhotoChange = useCallback(
    ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
      if (!files) return;
      attachManager.current?.uploadImages(files);
      setPhotoInputKey(Date.now());
    },
    [attachManager, setPhotoInputKey],
  );

  const handleVideoChange = useCallback(
    ({ target }) => {
      if (!target.files.length) {
        return;
      }

      attachManager.current?.uploadVideo(target.files[0]);
      setVideoInputKey(Date.now());
    },
    [attachManager],
  );

  const handlePriceChange = useCallback(
    ({ target }) => {
      dispatch(
        imActions.setWelcomeMessage({
          type,
          data: {
            ...welcomeMessage,
            price: formatInputAmount(target.value),
          },
        }),
      );
    },
    [welcomeMessage],
  );

  const handleTextChange = useCallback(
    ({ target }) => {
      dispatch(
        imActions.setWelcomeMessage({
          type,
          data: {
            ...welcomeMessage,
            text: target.value,
          },
        }),
      );
    },
    [welcomeMessage],
  );

  const priceTip = useMemo(() => {
    if (+price < 0.1 || !price) {
      return t('create_invoice_price_description', {
        amount: formatCurrency(0.1, CurrencyEnum.EUR),
      });
    }

    const resultPrice = new BigNumber(price).multipliedBy(0.9).toPrecision();
    let res = t('create_invoice_price_fee_tip', {
      fee: feePercent * 100,
      amount: resultPrice,
    });

    if (tonRate && tonRate > 0) {
      res += ` (~$${(tonRate * +resultPrice).toFixed(2)})`;
    }

    return res;
  }, [t, price, tonRate]);

  const handleEnabled = useCallback(() => {
    dispatch(
      imActions.setWelcomeMessage({
        type,
        data: {
          ...welcomeMessage,
          isEnabled: !welcomeMessage.isEnabled,
        },
      }),
    );
  }, [welcomeMessage]);

  const handlePaid = useCallback(() => {
    dispatch(
      imActions.setWelcomeMessage({
        type,
        data: {
          ...welcomeMessage,
          isPaid: !welcomeMessage.isPaid,
        },
      }),
    );
  }, [welcomeMessage]);

  const handleSend = useCallback(() => {
    dispatch(imActions.editWelcomeMessage(type));
  }, [type]);

  if (loadingStatus === 'loading') {
    return <Loader isWrapped />;
  }

  if (loadingStatus === 'failed') {
    return (
      <Placeholder
        centered
        title={t('failed_title')}
        actions={
          <Button size="small" onClick={loadData}>
            {t('failed_button')}
          </Button>
        }
      >
        {t('failed_caption')}
      </Placeholder>
    );
  }

  return (
    <>
      <ScrollView>
        <Form>
          <FormItem description={t(`welcome_message_${type}_description`)}>
            <List>
              <ListItem
                onClick={handleEnabled}
                after={<Switch checked={welcomeMessage.isEnabled} />}
                disableHighlight
              >
                {t(`welcome_message_${type}_enabled`)}
              </ListItem>
            </List>
          </FormItem>
          {welcomeMessage.isEnabled &&
            (type === WelcomeMessageType.FOLLOWER ||
              type === WelcomeMessageType.SPONSOR) && (
              <FormItem>
                <List>
                  <ListItem
                    onClick={handlePaid}
                    after={<Switch checked={welcomeMessage.isPaid} />}
                    disableHighlight
                  >
                    {t('welcome_message_paid')}
                  </ListItem>
                </List>
              </FormItem>
            )}
          {welcomeMessage.isEnabled && welcomeMessage.isPaid && (
            <FormItem title={t('create_invoice_price_label')} description={priceTip}>
              <Input
                placeholder={t('create_invoice_price_placeholder')}
                value={welcomeMessage.price}
                onChange={handlePriceChange}
              />
            </FormItem>
          )}
          {welcomeMessage.isEnabled && (
            <FormItem
              title={t('create_invoice_message_label')}
              description={
                welcomeMessage.isPaid ? t('create_invoice_message_input_tip') : null
              }
            >
              <Input
                multiline
                placeholder={
                  type === WelcomeMessageType.FOLLOWER
                    ? t('create_invoice_message_placeholder')
                    : undefined
                }
                value={welcomeMessage.text}
                onChange={handleTextChange}
              />
            </FormItem>
          )}
          {welcomeMessage.isEnabled && (
            <FormItem
              title={t('create_invoice_content_label')}
              description={t(
                type === WelcomeMessageType.TG_SUBSCRIBER
                  ? 'create_invoice_content_description_tg'
                  : 'create_invoice_content_description',
              )}
            >
              <div className="WelcomeMessage__attach_wrap">
                <AttachPickerManager
                  ref={attachManager}
                  pickerId={'welcome_message_' + type}
                />
              </div>
              {(type !== WelcomeMessageType.TG_SUBSCRIBER || !attachments.length) && (
                <div className="WelcomeMessage__select_button_wrap">
                  <div className="WelcomeMessage__select_button">
                    <Button size="small" mode="secondary" icon={<IcGallery28 />}>
                      {t('new_post_attach_photo')}
                    </Button>
                    <input
                      key={photoInputKey}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      multiple={type !== WelcomeMessageType.TG_SUBSCRIBER}
                    />
                  </div>
                  <div className="WelcomeMessage__select_button">
                    <Button size="small" mode="secondary" icon={<IcVideo28 />}>
                      {t('new_post_attach_video')}
                    </Button>
                    <input
                      key={videoInputKey}
                      type="file"
                      accept="video/mp4,video/quicktime"
                      onChange={handleVideoChange}
                    />
                  </div>
                </div>
              )}
            </FormItem>
          )}
        </Form>
      </ScrollView>
      <BottomBar>
        <Button onClick={handleSend} stretched>
          {t('save')}
        </Button>
      </BottomBar>
    </>
  );
};

export const WelcomeMessage: FC = () => {
  const { route } = useRoute();
  const isWalidType = [
    WelcomeMessageType.FOLLOWER,
    WelcomeMessageType.SPONSOR,
    WelcomeMessageType.TG_SUBSCRIBER,
  ].includes(route.params.type);
  const t = useTranslator();
  const type = isWalidType ? route.params.type : WelcomeMessageType.FOLLOWER;

  return (
    <>
      <Header showBackButton>{t('welcome_message_title')}</Header>
      {isWalidType ? (
        <WelcomeMessageInner type={type} />
      ) : (
        <>
          <ScrollView>
            <Form>
              <FormItem>
                <List>
                  <ListItem
                    onClick={() =>
                      goTo(WELCOME_MESSAGE, { type: WelcomeMessageType.FOLLOWER })
                    }
                    chevron
                  >
                    {t('im_bottom_sheet_wm_follower')}
                  </ListItem>
                  <ListItem
                    onClick={() =>
                      goTo(WELCOME_MESSAGE, { type: WelcomeMessageType.SPONSOR })
                    }
                    chevron
                  >
                    {t('im_bottom_sheet_wm_sponsor')}
                  </ListItem>
                  <ListItem
                    onClick={() =>
                      goTo(WELCOME_MESSAGE, { type: WelcomeMessageType.TG_SUBSCRIBER })
                    }
                    chevron
                  >
                    {t('im_bottom_sheet_wm_tg_subscriber')}
                  </ListItem>
                </List>
              </FormItem>
            </Form>
          </ScrollView>
        </>
      )}
    </>
  );
};
